import { useState } from "react";
import { Plus, Star, Trash2, Phone, PhoneCall } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useSentinel } from "@/context/SentinelContext";

export default function Contacts() {
  const { contacts, addContact, removeContact, setPrimary } = useSentinel();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", relationship: "", phone: "" });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;
    addContact(form);
    toast.success(`${form.name} added to emergency contacts`);
    setForm({ name: "", relationship: "", phone: "" });
    setOpen(false);
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-100">
            Emergency Contacts
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            People Sentinel notifies when an emergency escalation is confirmed.
          </p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-10 bg-sentinel-accent text-white hover:bg-sentinel-accenthover" data-testid="add-contact-button">
              <Plus className="mr-1.5 h-4 w-4" /> Add contact
            </Button>
          </DialogTrigger>
          <DialogContent className="border-sentinel-border bg-sentinel-card">
            <DialogHeader>
              <DialogTitle className="font-heading text-slate-100">New emergency contact</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="add-contact-form">
              <Field label="Full name" id="name">
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="contact-name-input"
                  className="border-sentinel-border bg-sentinel-deep text-slate-100 focus-visible:ring-sentinel-accent"
                  placeholder="e.g. Jordan Blake"
                />
              </Field>
              <Field label="Relationship" id="relationship">
                <Input
                  id="relationship"
                  value={form.relationship}
                  onChange={(e) => setForm({ ...form, relationship: e.target.value })}
                  data-testid="contact-relationship-input"
                  className="border-sentinel-border bg-sentinel-deep text-slate-100 focus-visible:ring-sentinel-accent"
                  placeholder="e.g. Friend"
                />
              </Field>
              <Field label="Phone number" id="phone">
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  required
                  data-testid="contact-phone-input"
                  className="border-sentinel-border bg-sentinel-deep text-slate-100 focus-visible:ring-sentinel-accent"
                  placeholder="+49 …"
                />
              </Field>
              <DialogFooter>
                <Button type="submit" className="bg-sentinel-accent text-white hover:bg-sentinel-accenthover" data-testid="save-contact-button">
                  Save contact
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 overflow-hidden rounded-lg border border-sentinel-border" data-testid="contacts-list">
        <ul className="divide-y divide-sentinel-border">
          {contacts.map((c) => (
            <li
              key={c.id}
              className="flex flex-col gap-4 bg-sentinel-card px-5 py-4 transition-colors hover:bg-[#1a1e2c] sm:flex-row sm:items-center"
              data-testid={`contact-item-${c.id}`}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded border border-sentinel-border bg-sentinel-deep font-mono text-sm font-semibold text-slate-200">
                {c.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
              </span>

              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-sm font-medium text-slate-100">
                  {c.name}
                  {c.primary && (
                    <span className="flex items-center gap-1 rounded bg-sentinel-accent/12 px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-sentinel-accent">
                      <Star className="h-2.5 w-2.5 fill-sentinel-accent" /> Primary
                    </span>
                  )}
                </p>
                <p className="font-mono text-[11px] uppercase tracking-wider text-slate-500">{c.relationship || "—"}</p>
              </div>

              <div className="flex items-center gap-6">
                <span className="flex items-center gap-2 font-mono text-sm text-slate-300">
                  <Phone className="h-3.5 w-3.5 text-slate-500" /> {c.phone}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => toast(`Test call to ${c.name} (${c.phone})`)}
                    className="rounded-md border border-sentinel-border p-2 text-slate-400 transition-colors hover:border-sentinel-borderhover hover:text-slate-100"
                    data-testid={`contact-testcall-${c.id}`}
                    aria-label={`Test call ${c.name}`}
                  >
                    <PhoneCall className="h-3.5 w-3.5" />
                  </button>
                  {!c.primary && (
                    <button
                      onClick={() => { setPrimary(c.id); toast.success(`${c.name} set as primary contact`); }}
                      className="rounded-md border border-sentinel-border p-2 text-slate-400 transition-colors hover:border-sentinel-borderhover hover:text-sentinel-accent"
                      data-testid={`contact-setprimary-${c.id}`}
                      aria-label={`Set ${c.name} as primary`}
                    >
                      <Star className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => { removeContact(c.id); toast(`${c.name} removed`); }}
                    className="rounded-md border border-sentinel-border p-2 text-slate-400 transition-colors hover:border-red-500/40 hover:text-red-400"
                    data-testid={`contact-delete-${c.id}`}
                    aria-label={`Remove ${c.name}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
        {contacts.length === 0 && (
          <div className="px-5 py-14 text-center text-sm text-slate-500" data-testid="contacts-empty-state">
            No emergency contacts yet. Add one to enable escalation.
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Field({ label, id, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-400">
        {label}
      </Label>
      {children}
    </div>
  );
}
